
import {
  FARE,
  PARAM_VALUE_DELIMITER,
  QUERY_PARAM_ALLOCATOR
} from 'contexts/CruiseSearchContext/constants';

/**
 * Takes applied search startDate range
 * and formats to be user readable
 * @param {string} dateRange e.g. ['2022-03-03~2022-04-20']
 * @param {array} options startDate filter options returned from graph
 * @return {string | undefined} User readable format e.g. 'March 3 - April 20 2022'
 */
export const formatStartDate = (dateRange, locale) => {
  const [start, end] = (dateRange || '').split('~');

  if (!start || !end) {
    return undefined;
  }

  const [sYear, sMonth, sDay] = start.split('-').map((str) => Number(str));
  const [eYear, eMonth, eDay] = end.split('-').map((str) => Number(str));

  if (!sYear || !eYear || !sMonth || !eMonth || !sDay || !eDay) {
    return undefined;
  }
  
  const showStartYear = sYear !== eYear;
  
  const startDate = new Date(sYear, sMonth - 1, sDay);
  const startFormat = showStartYear ? {month: 'long', day: 'numeric', year:'numeric'} : {month: 'long', day: 'numeric'};
  const startDateFormatted = startDate.toLocaleString(locale?.language, startFormat);

  const endDate = new Date(eYear, eMonth - 1, eDay);
  const endDateFormatted = endDate.toLocaleString( locale.language, {month: 'long', day: 'numeric', year:'numeric'});

  if (!startDateFormatted || !endDateFormatted) {
    return undefined;
  }

  return `${startDateFormatted} - ${endDateFormatted}`;
};

/**
 * @param {string} code filter code
 * @param {object} labelOptions e.g. { prepend: 'Departing from' }
 * @param {array} filterOption filterOption from graph
 * @return {string | undefined} The label that goes along with the code
 */
export const findFilterLabelFromCode = (code, labelOptions, filterOption) => {
  let currentOptions = [];
  const { groups, options } = filterOption || {};

  if (groups) {
    currentOptions = groups.flatMap((g) => g?.options).flat();
  }

  if (options) {
    currentOptions = options;
  }

  let { label } = currentOptions.find((o) => o?.code === code) || {};

  if (!label) {
    return undefined;
  }

  const { append, prepend } = labelOptions || {};
  const doAppend = append && typeof append === 'string';
  const doPrepend = prepend && typeof prepend === 'string';

  if (doPrepend) {
    label = `${prepend} ${label}`;
  }

  if (doAppend) {
    label += ` ${append}`;
  }

  return label;
};

/**
 * Formats a range filter value
 * e.g. nights=1~7
 * @param {string} range e.g. '1~7';
 * @param {object} labelOptions e.g. { append: 'Nights' }
 * - append: Optional string to append after the range
 * - prpend: Optional string to prpend before the range
 * @return {string | undefined} e.g. "1-7 Nights"
 */
export const makeRangeLabel = (range, labelOptions) => {
  const { append, prepend } = labelOptions || {};
  let label;

  const [start, end] = (range || '').split('~');
  if (range.includes('gte')) {
    return `${range.split('gte')[1]} + ${append}`;
  } else if (!range || !end || !start) {
    return undefined;
  }

  const doAppend = append && typeof append === 'string';
  const doPrepend = prepend && typeof prepend === 'string';

  label = range.replace(/~/g, '-');

  if (doPrepend) {
    label = `${prepend} ${label}`;
  }

  if (doAppend) {
    label += ` ${append}`;
  }
  return label;
};

export const removeQualifiersFromLabel = (label, qualifiersToRemove) => {
  if (!qualifiersToRemove.length || !label) {
    return label;
  }

  const labelParts = label.split(PARAM_VALUE_DELIMITER);
  const labelMap = labelParts.reduce((map, part) => {
    const [key, value] = part.split(QUERY_PARAM_ALLOCATOR);
    map[key] = value;
    return map;
  }, {});

  qualifiersToRemove.forEach(qualifier => {
    delete labelMap[qualifier];
  });

  const cleanedLabel = Object.keys(labelMap)
    .map(key => `${key}${QUERY_PARAM_ALLOCATOR}${labelMap[key]}`)
    .join(PARAM_VALUE_DELIMITER);

  return cleanedLabel;
};

// Deal with qualifiers
// const qualifiers = _filter(
//   filterOptions,
//   ({ __typename }) => __typename === 'CruiseBooleanFilter',
// );

export const makeQualifierLabel = (qualifier, labelOptions) => {
  const removedFareFromLabel = removeQualifiersFromLabel(qualifier, [FARE])
  const filters = removedFareFromLabel.split(QUERY_PARAM_ALLOCATOR);
  const prepend = labelOptions[filters[0]] || '';
  let newLabel = filters[1] ? `${prepend} ${filters[1]}` : filters[0];

  return newLabel;
};

export const makeCabinClassLabel = (cabinClass, dictionaries) => {
  return dictionaries.find(item => item.id === cabinClass)?.label || null;
};

export const makeOcupancyLabel = (occupancy, labelOptions) => {
  const filter = +(occupancy > 1);
  return `${occupancy} ${labelOptions[filter]}`;
};
